'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { buildLaravelApiUrl } from '@/lib/site';

interface ThirdPartyAdItem {
  id: number | string;
  ad_type?: string | null;
  ad_code?: string | null;
  banner_text?: string | null;
  subtitle_text?: string | null;
  button_text?: string | null;
  background_color?: string | null;
  link_url?: string | null;
}

interface ThirdPartyAdProps {
  placement: string;
  className?: string;
  categoryId?: number | string | null;
}

function normalizeBannerLink(raw: unknown): string | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (s.startsWith('/') || s.startsWith('#')) return s;
  if (/^mailto:/i.test(s) || /^tel:/i.test(s)) return s;
  if (/^https?:\/\//i.test(s) || s.startsWith('//')) return s;
  if (!/\s/.test(s) && s.includes('.')) return `https://${s}`;
  return null;
}

function linkOpensInNewTab(href: string): boolean {
  return /^https?:\/\//i.test(href) || href.startsWith('//');
}

/**
 * Preset class strings from Laravel admin (`ThirdPartyAds` Create/Edit).
 * They must exist as static literals in this repo so Tailwind JIT emits the CSS.
 * Values loaded only from the API are invisible to the scanner, so dynamic
 * `className={stored}` would otherwise drop gradients like `from-purple-600`.
 */
const ADMIN_BANNER_BACKGROUND_PRESETS = [
  'bg-gradient-to-r from-brand-orange to-red-500',
  'bg-gradient-to-r from-blue-600 to-purple-600',
  'bg-gradient-to-r from-green-600 to-emerald-600',
  'bg-gradient-to-r from-purple-600 to-pink-600',
  'bg-brand-orange',
  'bg-blue-600',
  'bg-green-600',
  'bg-purple-600',
  'bg-red-600',
  'bg-neutral-700',
] as const;

const DEFAULT_BANNER_BACKGROUND = ADMIN_BANNER_BACKGROUND_PRESETS[0];

function resolveBannerBackground(raw: string | null | undefined): string {
  if (raw == null || String(raw).trim() === '') return DEFAULT_BANNER_BACKGROUND;
  const s = String(raw).trim();
  return (ADMIN_BANNER_BACKGROUND_PRESETS as readonly string[]).includes(s)
    ? s
    : DEFAULT_BANNER_BACKGROUND;
}

function getSizeClasses(placement: string): string {
  switch (placement) {
    case 'homepage':
      return 'h-24 md:h-32 w-full';
    case 'category':
      return 'h-20 md:h-28 w-full';
    case 'category_middle':
      return 'h-32 md:h-40 w-full';
    case 'deal_detail':
      return 'h-24 md:h-32 w-full';
    case 'forum':
      return 'h-20 md:h-28 w-full';
    default:
      return 'h-24 w-full';
  }
}

function getPlaceholderContent(placement: string): {
  title: string;
  subtitle: string;
  color: string;
  buttonText: string;
} {
  switch (placement) {
    case 'category':
      return {
        title: 'Premium Ad Slot',
        subtitle: 'Target specific categories.',
        color: 'bg-neutral-700 text-white',
        buttonText: 'Advertise',
      };
    case 'category_middle':
      return {
        title: 'Advertise Here',
        subtitle: 'Contact sales@buyme.lk',
        color: 'bg-neutral-200 text-neutral-500',
        buttonText: 'Contact',
      };
    default:
      return {
        title: 'Advertise Here',
        subtitle: 'Contact sales@buyme.lk',
        color: 'bg-neutral-200 text-neutral-500',
        buttonText: 'Contact',
      };
  }
}

export function ThirdPartyAd({ placement, className = '', categoryId = null }: ThirdPartyAdProps) {
  const [device, setDevice] = useState<'mobile' | 'desktop'>(
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'mobile' : 'desktop',
  );
  const [ads, setAds] = useState<ThirdPartyAdItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const adContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onResize = () => {
      setDevice(window.innerWidth < 768 ? 'mobile' : 'desktop');
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const apiUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set('device', device);
    if (categoryId != null && String(categoryId).trim() !== '') {
      params.set('category_id', String(categoryId));
    }
    const query = params.toString();
    return buildLaravelApiUrl(`/third-party-ads/${encodeURIComponent(placement)}${query ? `?${query}` : ''}`);
  }, [placement, device, categoryId]);

  useEffect(() => {
    let cancelled = false;

    async function loadAds() {
      try {
        setLoading(true);
        setError(false);
        const res = await fetch(apiUrl, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!res.ok) {
          throw new Error(`Failed with status ${res.status}`);
        }
        const data = (await res.json()) as { ads?: ThirdPartyAdItem[] };
        if (!cancelled) {
          setAds(Array.isArray(data.ads) ? data.ads : []);
        }
      } catch {
        if (!cancelled) {
          setAds([]);
          setError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadAds();
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  useEffect(() => {
    if (!adContainerRef.current || ads.length === 0) return;

    const containers = adContainerRef.current.querySelectorAll('.third-party-ad-item');
    ads.forEach((ad, index) => {
      const container = containers[index] as HTMLDivElement | undefined;
      if (!container || container.hasAttribute('data-rendered')) return;

      try {
        container.innerHTML = '';
        const adWrapper = document.createElement('div');
        adWrapper.setAttribute('data-ad-id', String(ad.id));
        adWrapper.setAttribute('data-rendered', 'true');
        adWrapper.innerHTML = ad.ad_code ?? '';

        if (ad.ad_type === 'banner') {
          adWrapper.style.width = '100%';
          adWrapper.style.height = '100%';
          adWrapper.style.display = 'flex';
          adWrapper.style.alignItems = 'center';
          adWrapper.style.justifyContent = 'center';
          adWrapper.style.overflow = 'hidden';
          adWrapper.style.position = 'absolute';
          adWrapper.style.top = '0';
          adWrapper.style.left = '0';

          const images = adWrapper.querySelectorAll('img');
          images.forEach((img) => {
            img.style.width = '100%';
            img.style.height = '100%';
            img.style.objectFit = 'cover';
            img.style.display = 'block';
            img.style.margin = '0';
            img.style.padding = '0';
          });

          const links = adWrapper.querySelectorAll('a');
          links.forEach((link) => {
            link.style.display = 'block';
            link.style.width = '100%';
            link.style.height = '100%';
            link.style.textDecoration = 'none';
          });
        }

        container.appendChild(adWrapper);
        const scripts = adWrapper.querySelectorAll('script');
        scripts.forEach((oldScript) => {
          const newScript = document.createElement('script');
          Array.from(oldScript.attributes).forEach((attr) => {
            newScript.setAttribute(attr.name, attr.value);
          });
          if (oldScript.innerHTML) {
            newScript.appendChild(document.createTextNode(oldScript.innerHTML));
          }
          oldScript.parentNode?.replaceChild(newScript, oldScript);
        });

        container.setAttribute('data-rendered', 'true');
      } catch {
        // Keep this slot empty if ad script fails.
      }
    });
  }, [ads]);

  const placeholder = getPlaceholderContent(placement);
  const sizeClasses = getSizeClasses(placement);
  const isSimplePlaceholder = placeholder.title === 'Advertise Here';

  if (loading || error || ads.length === 0) {
    return (
      <div
        className={`relative overflow-hidden rounded-xl flex flex-col items-center justify-center text-center p-4 ${sizeClasses} ${placeholder.color} ${className} shadow-sm`}
        aria-busy={loading}
      >
        <span className="absolute top-0 right-0 bg-white/30 text-white text-[8px] px-1.5 py-0.5 rounded-bl font-medium backdrop-blur-sm">
          Ad
        </span>
        <div className={`z-10 relative ${isSimplePlaceholder ? 'text-neutral-500' : 'text-white'} w-full px-4`}>
          <h3 className="font-black text-sm md:text-xl leading-tight mb-1 tracking-tight">{placeholder.title}</h3>
          <p className="text-xs md:text-sm opacity-90 mb-3 hidden md:block font-medium">{placeholder.subtitle}</p>
          {!isSimplePlaceholder && (
            <span className="inline-block bg-white text-brand-dark text-[10px] md:text-xs font-bold px-4 py-1.5 rounded-full shadow-lg">
              {placeholder.buttonText}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={adContainerRef}
      className={`third-party-ad-container ${className}`}
      data-placement={placement}
      data-category-id={categoryId ?? ''}
    >
      {ads.map((ad) => {
        if (ad.ad_type === 'banner' && ad.banner_text && !ad.ad_code) {
          const bgColor = resolveBannerBackground(ad.background_color);
          const href = normalizeBannerLink(ad.link_url);
          const interactiveClass = href ? 'cursor-pointer transition-transform hover:scale-[1.01]' : 'cursor-default';

          const body = (
            <>
              <span className="absolute top-0 right-0 bg-white/30 text-white text-[8px] px-1.5 py-0.5 rounded-bl font-medium backdrop-blur-sm">
                Ad
              </span>
              <div className="z-10 relative text-white w-full px-4">
                <h3 className="font-black text-sm md:text-xl leading-tight mb-1 tracking-tight">{ad.banner_text}</h3>
                {ad.subtitle_text && (
                  <p className="text-xs md:text-sm opacity-90 mb-3 hidden md:block font-medium">{ad.subtitle_text}</p>
                )}
                {ad.button_text && (
                  <span className="inline-block bg-white text-brand-dark text-[10px] md:text-xs font-bold px-4 py-1.5 rounded-full shadow-lg">
                    {ad.button_text}
                  </span>
                )}
              </div>
            </>
          );

          if (href) {
            const newTab = linkOpensInNewTab(href);
            return (
              <a
                key={String(ad.id)}
                href={href}
                target={newTab ? '_blank' : '_self'}
                rel={newTab ? 'noopener noreferrer' : undefined}
                className={`relative overflow-hidden rounded-xl flex flex-col items-center justify-center text-center p-4 ${sizeClasses} ${bgColor} shadow-sm ${interactiveClass} mb-4 no-underline`}
              >
                {body}
              </a>
            );
          }

          return (
            <div
              key={String(ad.id)}
              className={`relative overflow-hidden rounded-xl flex flex-col items-center justify-center text-center p-4 ${sizeClasses} ${bgColor} shadow-sm ${interactiveClass} mb-4`}
            >
              {body}
            </div>
          );
        }

        return (
          <div
            key={String(ad.id)}
            className={`third-party-ad-item mb-4 relative overflow-hidden rounded-xl ${sizeClasses} flex items-center justify-center`}
            data-ad-id={String(ad.id)}
            data-ad-type={ad.ad_type ?? ''}
          >
            <span className="absolute top-0 right-0 bg-white/30 text-white text-[8px] px-1.5 py-0.5 rounded-bl font-medium backdrop-blur-sm z-10">
              Ad
            </span>
          </div>
        );
      })}
    </div>
  );
}
